import React, { useState } from 'react';
import { CheckCircle2, CircleDashed, FileText, XCircle } from 'lucide-react';
import { useTesterEvidenceChecklist } from '../../../hooks/analytics/useTesterReports.ts';
import { WidgetCard } from '../controller/shared.tsx';

const REVIEW_ICON: Record<string, React.ReactNode> = {
  approved: <CheckCircle2 className="h-4 w-4 text-green-500" />,
  reviewed: <CheckCircle2 className="h-4 w-4 text-green-500" />,
  rejected: <XCircle className="h-4 w-4 text-red-500" />,
  pending: <CircleDashed className="h-4 w-4 text-gray-400" />,
};

/**
 * Report #34 — Evidence Checklist. Unlike every other Tester report, this
 * one is keyed by a specific `run_id` rather than the caller's assignee id
 * — and none of the other Tester row shapes (TesterMyControlsRow,
 * TesterRerunsRequiredRow, etc.) expose a run id to source one from
 * automatically. Rendered here as a self-contained "enter a run id" section
 * so it never crashes with an unselected run; a tester gets the run id from
 * the test-run detail page for the control they're working.
 */
export const EvidenceChecklistGrid: React.FC = () => {
  const [runIdInput, setRunIdInput] = useState('');
  const [activeRunId, setActiveRunId] = useState<string | undefined>(undefined);
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useTesterEvidenceChecklist(activeRunId);
  const rows = data ?? [];

  return (
    <WidgetCard
      title="Evidence Checklist"
      description="Per-sample evidence review status for a selected test run"
      loading={!!activeRunId && isLoading}
      error={activeRunId && isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load the evidence checklist for that run.'}
      isEmpty={!!activeRunId && !isLoading && !isError && rows.length === 0}
      emptyMessage="No evidence samples found for that run."
      updatedAt={activeRunId ? dataUpdatedAt : undefined}
      onRefresh={activeRunId ? refetch : undefined}
      isFetching={isFetching}
    >
      <form
        className="flex items-center gap-2 mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          setActiveRunId(runIdInput.trim() || undefined);
        }}
      >
        <input
          type="text"
          value={runIdInput}
          onChange={(e) => setRunIdInput(e.target.value)}
          placeholder="Paste a test run id (UUID)…"
          className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={!runIdInput.trim()}
          className="shrink-0 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Load
        </button>
      </form>

      {!activeRunId ? (
        <div className="flex flex-col items-center justify-center py-10 text-center text-gray-400">
          <FileText className="h-6 w-6 mb-2" />
          <p className="text-sm">Select a run to view its evidence checklist.</p>
        </div>
      ) : (
        <div className="analytics-scroll overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
                <th className="py-2 pr-3">Record</th>
                <th className="py-2 pr-3">Attribute</th>
                <th className="py-2 pr-3">File</th>
                <th className="py-2 pr-3">Source</th>
                <th className="py-2 pr-3">Review Status</th>
                <th className="py-2 pr-3">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, idx) => (
                <tr key={`${row.record_ref}-${idx}`}>
                  <td className="py-2 pr-3 font-medium text-gray-900">{row.record_ref}</td>
                  <td className="py-2 pr-3 text-gray-600">{row.attribute_ref ?? '—'}</td>
                  <td className="py-2 pr-3 text-gray-600 truncate max-w-[160px]" title={row.file_name ?? undefined}>
                    {row.file_name ?? '—'}
                  </td>
                  <td className="py-2 pr-3 text-gray-600 capitalize">{row.source ?? '—'}</td>
                  <td className="py-2 pr-3">
                    <span className="inline-flex items-center gap-1.5 capitalize text-gray-700">
                      {REVIEW_ICON[(row.review_status ?? '').toLowerCase()] ?? (
                        <CircleDashed className="h-4 w-4 text-gray-300" />
                      )}
                      {row.review_status ?? 'Not reviewed'}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-gray-600">{row.confidence != null ? `${row.confidence}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </WidgetCard>
  );
};

export default EvidenceChecklistGrid;
